import {
	BOX_DRAW_SIDEBAR_WIDTH,
	HEADER_HEIGHT,
} from "@labeling-tools/out-of-stock/src/config/data";
import { getLetterForIndex } from "@labeling-tools/ui-components/src/config/letterIndexMap";
import { ActionRow } from "./ActionRow";
import { Button, Card, Tooltip } from "@mantine/core";
import { IconInfoCircle, IconCrosshair, IconUser } from "@tabler/icons-react";
import { UserDrawnBox } from "@labeling-tools/ui-components/src/box-draw-image/types";

interface BoxDrawSidebarProps {
	existingBoxes: { points: [number, number][] }[];
	userDrawnBoxes: UserDrawnBox[];
	hiddenExistingBoxIndices?: Set<number>;
	hiddenUserDrawnBoxIndices?: Set<number>;
	deletedBoxIndices?: Set<number>;
	selectedExistingBoxIndex?: number | null;
	selectedUserDrawnBoxIndex?: number | null;
	onBoxView?: (index: number, isUserDrawn?: boolean) => void;
	onBoxDelete?: (index: number, isUserDrawn?: boolean) => void;
	onBoxRestore?: (index: number) => void;
	onSubmit?: () => void;
	isSubmitting?: boolean;
}

export const BoxDrawSidebar = ({
	existingBoxes,
	userDrawnBoxes,
	hiddenExistingBoxIndices = new Set(),
	hiddenUserDrawnBoxIndices = new Set(),
	deletedBoxIndices = new Set(),
	selectedExistingBoxIndex,
	selectedUserDrawnBoxIndex,
	onBoxView,
	onBoxDelete,
	onBoxRestore,
	onSubmit,
	isSubmitting = false,
}: BoxDrawSidebarProps) => {
	return (
		<div
			className="absolute right-0 top-0 bottom-0"
			style={{
				top: HEADER_HEIGHT - 2,
				width: BOX_DRAW_SIDEBAR_WIDTH,
			}}
		>
			<Card className="h-full px-6 flex flex-row justify-between">
				<span className="flex flex-row justify-between">
					<h2 className="justify-start text-left text-xl font-semibold">
						Detections
					</h2>
					<Tooltip
						label={
							<div>
								<p>Auto detections cannot be moved, only deleted.</p>
								<ul>
									<li>- Blue box: auto detection</li>
									<li>- Purple box: user added detection</li>
								</ul>
								<p>
									On the image, click a user added detection box to select it.
								</p>
								<p>
									pressing backspace/delete will remove it from the detections
									list.
								</p>
							</div>
						}
					>
						<IconInfoCircle className="my-auto" size={18} />
					</Tooltip>
				</span>
				<div className="relative mt-4 overflow-y-scroll max-h-[50%]">
					<span className="bg-white sticky top-0 flex flex-row justify-start">
						<IconCrosshair className="my-auto mr-2" size={18} color="purple" />
						<h3 className="justify-start text-left text-xl font-semibold">
							Empty Area(s)
						</h3>
					</span>
					{/* Existing boxes */}
					{existingBoxes.map((_, index) => {
						const isDeleted = deletedBoxIndices.has(index);
						return (
							<ActionRow
								key={`box_${index}`}
								index={index}
								letter={getLetterForIndex(index)}
								userDrawn={false}
								isSelected={selectedExistingBoxIndex === index}
								isHidden={hiddenExistingBoxIndices.has(index)}
								isDeleted={isDeleted}
								onView={() => onBoxView?.(index, false)}
								onDelete={() => onBoxDelete?.(index, false)}
								onRestore={() => onBoxRestore?.(index)}
							/>
						);
					})}

					{/* User-drawn boxes */}
					{userDrawnBoxes.map((box, index) => {
						if (box.type !== "draw") return null;
						return (
							<ActionRow
								key={`user-drawn-box-draw-${index}`}
								index={index}
								letter={
									box.letter ?? getLetterForIndex(existingBoxes.length + index)
								}
								userDrawn={true}
								isSelected={selectedUserDrawnBoxIndex === index}
								isHidden={hiddenUserDrawnBoxIndices.has(index)}
								onView={() => onBoxView?.(index, true)}
								onDelete={() => onBoxDelete?.(index, true)}
							/>
						);
					})}
				</div>

				<div className="relative mt-4 overflow-y-scroll max-h-[50%]">
					<span className="bg-white sticky top-0 flex flex-row justify-start">
						<IconUser className="my-auto mr-2" size={18} color="orange" />
						<h3 className="justify-start text-left text-xl font-semibold">
							Unblurred Person(s)
						</h3>
					</span>
					{/* User-drawn boxes */}
					{userDrawnBoxes.map((box, index) => {
						if (box.type !== "draw-user") return null;
						return (
							<ActionRow
								key={`user-drawn-box-draw-user-${index}`}
								index={index}
								letter={
									box.letter ?? getLetterForIndex(existingBoxes.length + index)
								}
								userDrawn={true}
								isSelected={selectedUserDrawnBoxIndex === index}
								isHidden={hiddenUserDrawnBoxIndices.has(index)}
								onView={() => onBoxView?.(index, true)}
								onDelete={() => onBoxDelete?.(index, true)}
							/>
						);
					})}
				</div>

				<Button
					size="md"
					onClick={onSubmit}
					className="mt-auto min-w-[120px] min-h-[40px]"
					loading={isSubmitting}
				>
					Submit
				</Button>
			</Card>
		</div>
	);
};
